import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { setAppLoading } from "actions/appStateActions";
import { setPayableSession } from "actions/payableSessionActions";
import { ACTION } from "actions/paymentIntentActions";
import { USER_LOGOUT } from "actions/userActions";
import IamApi from "api/IamApi";
import {
  getPayableFromSession,
  postPayablePrepareIntent,
} from "api/IamApi/entities/ezpay";
import CircleInfo from "assets/icons/info_icon_solid.svg";
import XButton from "assets/icons/x_button.svg";
import { ContactAndTocFooter } from "components/ezpay/common/contact-and-toc-footer";
import { DashboardSkeleton } from "components/ezpay/common/dashboard-skeleton";
import { PageLoader } from "components/ezpay/common/page-loader";
import { SkeletonItem } from "components/ezpay/common/skeleton-item";
import { PayableCardHeader } from "components/ezpay/payables/payable-card-header";
import { customStyles } from "constants/customStyles";
import FundingOption, {
  ApplePay,
  getCardNetworkByShortCode,
  GooglePay,
  PayPal,
  translateBankAccountType,
  Venmo,
} from "constants/FundingOption";
import {
  BALANCE_NAMESPACE,
  COMMON_NAMESPACE,
  PAYMENT_METHOD_MODAL_NAMESPACE,
  PAYMENT_RECEIPT_NAMESPACE,
} from "constants/i18n-namespaces";
import {
  useCurrencyFormatter,
  useDateFormatter,
  useLabelsWithKrn,
  usePaymentHistory,
  useRetailer,
} from "hooks";
import useWallet from "hooks/useWallet";
import {
  Banner,
  BannerProps,
  Button,
  ButtonRow,
  Tooltip,
  TooltipEnums,
} from "kubra-ux-forge";
import { CardType } from "kubra-ux-forge/dist/foundation/CreditCardLogos/CreditCardLogo";
import _ from "lodash";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { getI18n, useTranslation } from "react-i18next";
import Modal from "react-modal";
import Sheet from "react-modal-sheet";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router";
import { Link } from "react-router-dom";
import routing from "routing";
import { getBiller } from "selectors";
import { getIsMobile } from "selectors/appState";
import { getPayablePaymentIntent } from "selectors/payablePaymentIntent";
import { getPayableSession } from "selectors/payableSession";
import {
  getBankAccount,
  getCreditDebitCard,
  getPaymentIntent,
  getPaymentMethod,
  getSelectedAmountOptionKey,
  getWallet,
} from "selectors/paymentIntent";
import { IDonation } from "types/Api/Payment/Donation";
import { payablePaymentIntentPrepareRequest } from "types/Api/Payment/PayablePaymentIntentPrepareRequest";
import { ICharityState } from "types/charity";
import { DigitalPaymentData } from "types/digitalPaymentData";
import {
  AccountPayable,
  InvoicePayable,
  PayableAmountOption,
} from "types/payable";
import { IPayableSessionData } from "types/payableSession";
import { IDigitalWallet } from "types/paymentIntent";
import {
  getDomain,
  getLabelWithKrn,
  mapCharityToCharityState,
  mapUiLanguageToSessionLanguage,
  phoneFormat,
} from "utilities/helper";
import { Donation } from "../../common/donation";
import { ManagePaymentMethodsModal } from "../../common/manage-payment-methods-modal";
import { PayableAmountOptionsModal } from "../../common/payable-amount-options-modal";
import { PayableApplePayButtonWrapper } from "../../common/payable-apple-pay-button";
import { PayableBankAccountModal } from "../../common/payable-bank-account-modal";
import {
  isExpiryCardDateValid,
  PayableCreditDebitModal,
} from "../../common/payable-credit-debit-modal";
import { PayableGooglePayButtonWrapper } from "../../common/payable-google-pay-button";
import PayablePayPalButtonWrapper from "../../common/payable-paypal-button/payable-paypal-button-wrapper";
import { MAX_RECENT_PAYMENTS } from "../../common/payable-recent-payments";
import { PaymentAmountModal } from "../../common/payment-amount-modal";
import { PaymentDateModal } from "../../common/payment-date-modal";
import { IReceiptInfo } from "../../common/receipt-info-modal";
import { CashModal } from "../../common/retail-cash-flow/cash-modal";
import { CashView } from "../../common/retail-cash-flow/cash-view";
import { RetailerView as RetailerModal } from "../../common/retail-cash-flow/retailer-view";
import { SelectPaymentMethodModal } from "../../common/select-payment-method-modal";
import { UnableToLoadAmountsModal } from "../../common/unable-to-load-amounts-modal";
import { ErrorPayableDashboard } from "../error-payable-dashboard";

//components for displaying the various views within payment method modal
const methodComponents = {
  SelectPaymentMethodModal,
  CashModal,
  RetailerModal,
  PayableBankAccountModal,
  PayableCreditDebitModal,
};
export type MethodComponentsOptions = keyof typeof methodComponents;

export const castToCardType = (
  cardTypeString: string,
): CardType | undefined => {
  try {
    const cardType: CardType = cardTypeString.toLowerCase() as CardType;
    return cardType;
  } catch (error) {
    return undefined;
  }
};

enum PaymentAllowed {
  NotDefined,
  AnyAmount,
  BilledAmountOrLess,
  BilledAmountOnly,
  NoPaymentAllowed,
}

const DIGITAL_PAYMENT_METHODS = new Set([
  FundingOption.GooglePay,
  FundingOption.ApplePay,
  FundingOption.PayPal,
  FundingOption.Venmo,
]);

const PAYMENT_METHOD_DATA_CY: Record<string, string> = {
  [GooglePay]: "button-google-pay",
  [ApplePay]: "button-apple-pay",
  [PayPal]: "button-paypal",
  [Venmo]: "button-venmo",
};

export const PayableDashboard = () => {
  const { t } = useTranslation(BALANCE_NAMESPACE);
  const isMobile = useSelector(getIsMobile);

  const { handleSubmit } = useForm();

  const payableSession = useSelector(getPayableSession);

  const isFreeform = payableSession?.payable?.category === "freeform";

  const isMultiBusinessUnitSupported =
    payableSession?.payableType?.supportedBusinessUnitCodes &&
    payableSession?.payableType?.supportedBusinessUnitCodes.length > 1;

  const payableAmountOptions = payableSession?.payable?.payableAmountOptions;
  // Multiple payment options are only supported for invoice payables, not accounts.
  const hasMultiplePaymentOptions =
    payableSession?.payable?.category === "invoice" &&
    payableAmountOptions != null;
  const amountSelection = hasMultiplePaymentOptions
    ? payableAmountOptions
    : undefined;

  // The lookup response resolved a selection group, but it carries no options to choose from.
  const amountOptionsAreEmpty = amountSelection?.options.length === 0;

  // Pre-select whichever option the user already chose in this workflow (kept in Redux, not the session).
  const persistedAmountOptionKey = useSelector(getSelectedAmountOptionKey);
  // A lone option has nothing to choose between, so it's selected automatically.
  const onlyAmountOption =
    amountSelection?.options.length === 1 ? amountSelection.options[0] : null;
  const preselectedAmountOption =
    amountSelection?.options.find(
      (option) => option.optionKey === persistedAmountOptionKey,
    ) ?? onlyAmountOption;

  const [selectedAmountOption, setSelectedAmountOption] =
    useState<PayableAmountOption | null>(preselectedAmountOption);

  const amountPending = hasMultiplePaymentOptions && !selectedAmountOption;

  // With multiple payment options the amount due is the selected option's amount,
  // since payableAmount isn't set on the payable.
  const amountDue = hasMultiplePaymentOptions
    ? (selectedAmountOption?.optionAmount ?? 0)
    : (payableSession?.payable?.payableAmount ?? 0);

  const [amountModalIsOpen, setAmountModalIsOpen] = useState(false);
  const [unableToLoadAmountsModalIsOpen, setUnableToLoadAmountsModalIsOpen] =
    useState(false);

  useEffect(() => {
    setUnableToLoadAmountsModalIsOpen(amountOptionsAreEmpty);
  }, [amountOptionsAreEmpty]);
  const [methodModalIsOpen, setMethodModalIsOpen] = useState(false);
  const [dateModalIsOpen, setDateModalIsOpen] = useState(false);
  const [retailerModalIsOpen, setRetailerModalIsOpen] = useState(false);

  const [managePaymentMethodModalIsOpen, setManagePaymentMethodModalIsOpen] =
    useState(false);

  // TODO: initialize current payment amount to the payable amount.
  // Consider if we need to initialize it to what's in the Redux store?
  // For instance when we come back from review page to payment page,
  // we would like to see the amount that the user entered instead of
  // defaulting to the payable amount.
  // UPDATE #2: More and more leaning towards having the current payment amount be kept in Redux and read from there
  // instead of duplicating it in local state here in this component. Need to get back to working on this asap.
  const paymentIntentState = useSelector(getPaymentIntent);
  const [currentPaymentAmount, setCurrentPaymentAmount] = useState(
    payableSession?.payableItemResponse?.paymentAmount ??
      preselectedAmountOption?.optionAmount ??
      payableSession?.payable?.payableAmount ??
      0.0,
  );

  const [activeMethodComponent, setActiveMethodComponent] =
    useState<MethodComponentsOptions>("SelectPaymentMethodModal");

  const [bannerState, setBannerState] = useState<BannerProps>({
    active: false,
    variant: "info",
    messageLabel: "",
  });

  const [displayMethodBack, setDisplayMethodBack] = useState(false);
  const [paymentErrorMessage, setPaymentErrorMessage] = useState("");
  const [paymentMethodError, setPaymentMethodError] = useState("");

  const [isDataLoading, setIsDataLoading] = useState(true);

  const [loadingStates, setLoadingStates] = useState({
    loadConfigWalletPayments: true,
    setWalletAsDefaultPaymentMethod: true,
    loadRecentUpcomingPayments: true,
  });

  const [paymentAllowedState, setPaymentAllowedState] =
    useState<PaymentAllowed>(PaymentAllowed.NotDefined);

  const currencyFormatter = useCurrencyFormatter();
  const dateFormatter = useDateFormatter();
  const navigate = useNavigate();

  const dispatch = useDispatch();

  // Persist the auto-selected option so the review screen and prepare request see it too.
  const onlyAmountOptionKey = onlyAmountOption?.optionKey ?? null;
  useEffect(() => {
    if (
      onlyAmountOptionKey &&
      persistedAmountOptionKey !== onlyAmountOptionKey
    ) {
      dispatch({
        type: ACTION.ADD_SELECTED_AMOUNT_OPTION_KEY,
        payload: onlyAmountOptionKey,
      });
    }
  }, [onlyAmountOptionKey, persistedAmountOptionKey, dispatch]);

  const finalBiller = useSelector(getBiller);
  const { loadLabelsWithKrn, getLabelsWithKrn } = useLabelsWithKrn();
  const labelSourceWithKrn = getLabelsWithKrn();

  const { loadBillerConfiguration, getBillerConfiguration } = useRetailer();
  const { loadWalletItems, getWalletItems } = useWallet(true);
  const {
    loadRecentPayments,
    loadUpcomingPayment,
    getUpcomingPayment,
    getRecentPayments,
  } = usePaymentHistory(true);

  const walletItems = getWalletItems();
  const bankAccount = useSelector(getBankAccount);
  const creditDebitCard = useSelector(getCreditDebitCard);
  const wallet = useSelector(getWallet);
  const paymentMethod = useSelector(getPaymentMethod);
  const billerConfiguration = getBillerConfiguration();
  const upcomingPayment = getUpcomingPayment();
  const recentPayments = getRecentPayments();

  const CurrentMethodComponent: any = methodComponents[activeMethodComponent];

  // TODO: review the payable payment intent state object here.
  // It doesn't match the payment intent state in the old flow.
  // Here it's matching the response of the payable initiate payment intent endpoint.
  // Whereas the old payment intent state holds more state (i.e. charities, current UI payment amount etc.)
  // The options for us seems to be:
  // 1) eliminate the payable payment intent and use the old payment intent state and just keep updating things in it from the new endpoints.
  // 2) update the payable payment intent state to match the old one. Though consider if there really is any benefit to this? Like would there be
  // any difference between the two "payment intent" states?
  const payablePaymentIntentState = useSelector(getPayablePaymentIntent);
  // TODO: review the use of the payment intent state. That state is used in the old flow.
  // But I'm utilizing it here because some of the components (like payment date modal) update their
  // state in the Redux store (and not through callbacks), so to get the selected payment date we have
  // to get from Redux store. The other options are we create a new payable payment date modal that
  // returns either the selected date through a callback or updates the state in the new "payable payment intent state".
  const todayDateString = dateFormatter.shortIsoFormat(new Date(), "en-US");
  const i18n = getI18n();
  const [emailBannerState, setEmailBannerState] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [smsBannerState, setSmsBannerState] = useState(false);
  const [isCashView, setIsCashView] = useState(false);
  const isDigitalPaymentSelected = DIGITAL_PAYMENT_METHODS.has(
    paymentIntentState.paymentMethod,
  );

  const providerTypeByFundingOption: Record<
    string,
    "paypal" | "googlepay" | "applepay" | "venmo"
  > = {
    [FundingOption.GooglePay]: "googlepay",
    [FundingOption.ApplePay]: "applepay",
    [FundingOption.PayPal]: "paypal",
    [FundingOption.Venmo]: "venmo",
  };

  // Set paymentIntentState.paymentDate to current date on mount
  useEffect(() => {
    if (paymentIntentState) {
      dispatch({
        type: ACTION.ADD_PAYMENT_DATE,
        payload: new Date(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const payableType = payableSession?.payableType;

  const updatePaymentAllowedState = (currentFinalAmountDue: number) => {
    if (payableType.allowPartialPayment) {
      //overpayment allowed
      if (payableType.allowOverPayment) {
        // any amount can be paid
        if (currentFinalAmountDue > 0) {
          setPaymentAllowedState(PaymentAllowed.AnyAmount);
          return;
        } else {
          // any amount can be paid, also show banner on zero balance or credit
          setPaymentAllowedState(PaymentAllowed.AnyAmount);
          setBannerState({
            active: true,
            variant: "info",
            messageLabel: t("no-payment-due-overpayment-allowed"),
            withClose: true,
            withHyperlink: false,
            withIcon: true,
          });
        }
      }
      // overpayment not allowed
      else {
        if (currentFinalAmountDue > 0) {
          // Billed amount or less can be paid
          setPaymentAllowedState(PaymentAllowed.BilledAmountOrLess);
        } else {
          // No payment allowed
          // hide the payment details form
          setPaymentAllowedState(PaymentAllowed.NoPaymentAllowed);
          setBannerState({
            active: true,
            variant: "info",
            messageLabel: t("no-payment-due-overpayment-not-allowed"),
            withClose: true,
            withHyperlink: false,
            withIcon: true,
          });
        }
      }
    }
    // partial payment not allowed
    // no need to check overpayment value
    else {
      if (currentFinalAmountDue > 0) {
        // Billed amount only can be paid
        setPaymentAllowedState(PaymentAllowed.BilledAmountOnly);
      } else {
        // No payment allowed
        // hide the payment details form
        setPaymentAllowedState(PaymentAllowed.NoPaymentAllowed);
        setBannerState({
          active: true,
          variant: "info",
          messageLabel: t("no-payment-due-overpayment-not-allowed"),
          withClose: true,
          withHyperlink: false,
          withIcon: true,
        });
      }
    }
  };

  const updateLoadingState = (key: string, value: boolean) => {
    setLoadingStates((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  useEffect(() => {
    if (
      !finalBiller?.clientId ||
      !finalBiller?.realm ||
      !finalBiller?.instanceId
    ) {
      return;
    }

    const domain = getDomain();
    const { clientId, realm, instanceId } = finalBiller;
    const language = mapUiLanguageToSessionLanguage(
      i18n.resolvedLanguage ?? "en",
    );

    loadLabelsWithKrn(
      clientId,
      instanceId,
      domain,
      realm,
      language,
      finalBiller.payableTypeDefinitions ?? [],
      finalBiller.featureFlags?.payableUi ?? "false",
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalBiller, i18n.resolvedLanguage]);

  // Clear error message when user changes the language
  useEffect(() => {
    setPaymentErrorMessage("");
    // scrolls to top in Safari
    document.body.scrollTop = 0;
    // scrolls to top in Chrome, Firefox, IE, and Opera
    document.documentElement.scrollTop = 0;
    // Mobile view
    window.scrollTo({ top: 0, behavior: "smooth" });

    setBannerState((prev) => ({
      ...prev,
      active: false,
      variant: "error",
      messageLabel: "",
    }));
  }, [i18n.language]);

  useEffect(() => {
    if (
      !bankAccount &&
      !creditDebitCard &&
      !wallet &&
      !(paymentMethod == GooglePay) &&
      !(paymentMethod == ApplePay) &&
      !(paymentMethod == PayPal) &&
      !(paymentMethod == Venmo) &&
      walletItems &&
      walletItems.length > 0
    ) {
      const preferredWallet =
        walletItems.find((w) => w.isPreferred) || walletItems[0];

      const isRestricted =
        payableSession?.payable?.fundingRestrictions?.includes(
          preferredWallet?.fundingOption ?? "",
        );

      if (preferredWallet !== undefined && !isRestricted) {
        dispatch({ type: ACTION.ADD_WALLET, payload: preferredWallet });
        dispatch({
          type: ACTION.ADD_PAYMENT_METHOD,
          payload: FundingOption.Wallet,
        });
      } else {
        dispatch({ type: ACTION.ADD_WALLET, payload: null });
        dispatch({
          type: ACTION.ADD_PAYMENT_METHOD,
          payload: "",
        });
      }
      updateLoadingState("setWalletAsDefaultPaymentMethod", false);
    } else {
      // If walletItems is empty or undefined, still mark loading as done
      updateLoadingState("setWalletAsDefaultPaymentMethod", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletItems, bankAccount, creditDebitCard, wallet]);

  useEffect(() => {
    // Load config and wallet
    const loadConfigWalletPayments = async () => {
      if (finalBiller !== null && finalBiller !== undefined) {
        try {
          await Promise.all([
            loadBillerConfiguration(finalBiller.clientId, finalBiller.realm),
            loadWalletItems(
              finalBiller.clientId,
              finalBiller.featureFlags.accountV2,
            ),
          ]);
        } catch {
          // suppress error — loading state is cleared in finally
        } finally {
          updateLoadingState("loadConfigWalletPayments", false);
        }
      } else {
        updateLoadingState("loadConfigWalletPayments", false);
      }
    };

    loadConfigWalletPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalBiller]);

  useEffect(() => {
    //load recent and upcoming payments
    const loadRecentUpcomingPayments = async () => {
      if (finalBiller !== null && finalBiller !== undefined) {
        try {
          await Promise.all([
            loadRecentPayments(
              MAX_RECENT_PAYMENTS,
              finalBiller.clientId,
              finalBiller.realm,
            ),
            loadUpcomingPayment(finalBiller.clientId, finalBiller.realm),
          ]);
        } catch {
          // suppress error — loading state is cleared in finally
        } finally {
          updateLoadingState("loadRecentUpcomingPayments", false);
        }
      } else {
        updateLoadingState("loadRecentUpcomingPayments", false);
      }
    };

    loadRecentUpcomingPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalBiller]);

  useEffect(() => {
    // When cash payment is selected, set the payment amount to the full payable amount
    if (isCashView) {
      setCurrentPaymentAmount(amountDue);
    }
  }, [isCashView, amountDue]);

  useEffect(() => {
    // scroll to top when banner shows up
    if (bannerState.active) {
      // scrolls to top in Safari
      document.body.scrollTop = 0;
      // scrolls to top in Chrome, Firefox, IE, and Opera
      document.documentElement.scrollTop = 0;
      // Mobile view
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [bannerState.active]);

  // Monitoring loadingStates and updating isDataLoading accordingly
  useEffect(() => {
    const allDone = Object.keys(loadingStates)
      .filter((key) => key !== "loadRecentUpcomingPayments")
      .every(
        (key) => loadingStates[key as keyof typeof loadingStates] === false,
      );
    if (allDone && payableSession) {
      setIsDataLoading(false);
    }
  }, [loadingStates, payableSession]);

  // TODO: move this away from useEffect. Should be able to do that as part of the render cycle.
  // Update payment allowed state
  useEffect(() => {
    if (payableSession?.payable) {
      // The real amount due isn't known yet when a contract-option selection is
      // pending, but payment should still be treated as allowed (not "no payment
      // due") rather than driven by the still-null payableAmount.
      updatePaymentAllowedState(
        hasMultiplePaymentOptions
          ? 1
          : (payableSession?.payable?.payableAmount ?? 0),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (paymentIntentState.charities) {
      // Deselect all charities
      paymentIntentState.charities?.forEach((c) =>
        updateCharity(c.charity.code, { selected: false }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const paymentMethodLabel = useMemo(() => {
    if (isCashView) {
      return t("cash-method");
    } else if (bankAccount != null) {
      return (
        translateBankAccountType(bankAccount.accountType, t) +
        " *" +
        bankAccount.bankAccountNumber.substring(
          bankAccount.bankAccountNumber.length - 4,
        )
      );
    } else if (creditDebitCard != null) {
      return (
        getCardNetworkByShortCode(creditDebitCard.cardType) +
        " *" +
        creditDebitCard.cardNo.substring(creditDebitCard.cardNo.length - 4)
      );
    } else if (wallet != null) {
      let label = "";
      if (wallet.fundingOption === FundingOption.Card) {
        label =
          getCardNetworkByShortCode(wallet.presentableMOPCode) +
          " " +
          wallet.maskedNumber;
        if (!isExpiryCardDateValid(wallet.expiry)) {
          setBannerState({
            active: true,
            variant: "warning",
            messageLabel: t("card-expired-warning", { card: label }),
            withClose: true,
            withIcon: true,
            withHyperlink: true,
            hyperlink: () => setManagePaymentMethodModalIsOpen(true),
            hyperlinkText: t("manage-payment-methods-button", {
              ns: PAYMENT_METHOD_MODAL_NAMESPACE,
            }),
          });
          // setWarningMessage(t("card-expired-warning", { card: label }));
          // reset wallet
          dispatch({ type: ACTION.ADD_WALLET, payload: null });
          dispatch({
            type: ACTION.ADD_PAYMENT_METHOD,
            payload: "",
          });
        }
      } else {
        // translate bank account type
        label =
          translateBankAccountType(wallet.presentableMOPCode, t) +
          " " +
          wallet.maskedNumber;
      }
      return label;
    }

    // Map all digital methods to empty label for display
    if (DIGITAL_PAYMENT_METHODS.has(paymentMethod)) {
      return "";
    }

    return t("select-one");
  }, [
    isCashView,
    bankAccount,
    creditDebitCard,
    wallet,
    paymentMethod,
    t,
    dispatch,
  ]);

  // Get charities.
  const charities = payableSession?.charities?.filter(
    (ch: any) => ch.code != null && ch.name != null,
  );

  const [charitiesState, setCharitiesState] = useState(
    mapCharityToCharityState(charities),
  );

  if (!payableSession.payable) {
    console.error(
      "Payable is not available in the session or the intent is not initialized",
    );

    return <Navigate to={routing.index()} replace />;
  }

  const setAmountModalIsOpenToTrue = () => {
    if (amountOptionsAreEmpty) {
      setUnableToLoadAmountsModalIsOpen(true);
    } else {
      setAmountModalIsOpen(true);
    }
  };

  const handleRetryAmountOptions = () => {
    dispatch(setAppLoading(true));
    getPayableFromSession(getDomain())
      .then((response: IPayableSessionData) => {
        dispatch(setPayableSession(response));
      })
      .catch((err) => {
        console.error(
          "Error refetching payable session for amount options",
          err,
        );
      })
      .finally(() => dispatch(setAppLoading(false)));
  };

  const setMethodModalIsOpenToTrue = () => {
    setDisplayMethodBack(false);
    setMethodModalIsOpen(true);
    setBannerState((prevState) => ({ ...prevState, active: false }));
  };

  const setAmountModalIsOpenToFalse = () => {
    document.body.style.removeProperty("overflow");
    setAmountModalIsOpen(false);
    setPaymentErrorMessage("");
    setBannerState((prevState) => ({ ...prevState, active: false }));
  };

  const setMethodModalIsOpenToFalse = () => {
    document.body.style.removeProperty("overflow");
    setMethodModalIsOpen(false);

    setActiveMethodComponent("SelectPaymentMethodModal");
    setPaymentMethodError("");
  };

  const setRetailerModalStatus = () => {
    if (activeMethodComponent === "RetailerModal") {
      setRetailerModalIsOpen(true);
    }
  };

  const updateCurrentPaymentAmount = (amount: number) => {
    // TODO: do we need to update the payment amount in the intent state?
    // Basically we need to ask our selves what's the source of truth for the current payment amount?
    // The state variable in this component or the Redux store?
    // The payment intent state in Redux is also kind of bloated holding state that doesn't
    // really belong to a "payment intent" (as in the Payment Intent API). For instance charities are
    // not part of a payment intent. Likewise the current payment amount is a property of the UI state
    // not "payment intent" state so it shouldn't even be in the "payment intent state" in the first place.
    setCurrentPaymentAmount(amount);
    setPaymentErrorMessage("");
    setBannerState((prev) => ({
      ...prev,
      active: false,
      variant: "error",
      messageLabel: "",
    }));
    setAmountModalIsOpenToFalse();
  };

  const handleMethodChange = (method: MethodComponentsOptions) => {
    setActiveMethodComponent(method);
    if (method === "SelectPaymentMethodModal") {
      setDisplayMethodBack(false);
    } else setDisplayMethodBack(true);
  };

  const paymentDateClicked = () => {
    if (futureDateDisabled) return;

    setDateModalIsOpen(true);
  };

  // get required values from subresources
  const imageResources: any = finalBiller?.properties?.find((element: any) => {
    return element.name === "ImageResources";
  });

  const externalLinks: any = finalBiller?.properties?.find((element: any) => {
    return element.name === "ExternalLinks";
  });

  const contactUs: any = finalBiller?.properties?.find((element: any) => {
    return element.name === "InstanceConfiguration";
  });

  const handleCharitySelection = (charityCode: string, selected: boolean) => {
    updateCharity(charityCode, { selected });
  };

  const handleCharityAmountChange = (charityCode: string, amount: number) => {
    updateCharity(charityCode, { amount });
  };

  const updateCharity = (
    charityCode: string,
    updateValue: Partial<ICharityState>,
  ) => {
    if (updateValue.selected !== undefined && !updateValue.selected) {
      updateValue.amount = 0;
    }

    const newCharitiesState = charitiesState.map((charityState) => {
      return charityState.charity.code !== charityCode
        ? charityState
        : {
            ...charityState,
            ...updateValue,
          };
    });
    setCharitiesState(newCharitiesState);
    dispatch({ type: ACTION.ADD_CHARITIES, payload: newCharitiesState });
  };

  // Add charities amount
  const charitiesTotal = charitiesState
    .filter((ch) => ch.selected)
    .reduce((amount, charity) => amount + charity.amount, 0);

  const getSelectedCharities = (): IDonation[] | undefined => {
    if (
      paymentIntentState.charities == null ||
      paymentIntentState.charities?.length == 0
    ) {
      return undefined;
    }

    return paymentIntentState.charities
      .filter((c) => c.selected && c.amount > 0)
      .map((selectedCharity) => ({
        charityCode: selectedCharity.charity.code,
        amount: selectedCharity.amount,
      }));
  };

  // Tell the backend which payment option was chosen (multiple payment options only).
  const selectedOptionKeyPayload =
    hasMultiplePaymentOptions && selectedAmountOption
      ? { selectedOptionKey: selectedAmountOption.optionKey }
      : {};

  const getPayablePrepareRequest =
    (): payablePaymentIntentPrepareRequest | null => {
      let selectedFsOption = "";
      if (bankAccount != null) {
        selectedFsOption = FundingOption.BankAccount;
      } else if (creditDebitCard != null) {
        selectedFsOption = FundingOption.Card;
      } else if (wallet != null) {
        selectedFsOption = FundingOption.Wallet;
      }

      // TODO: review if we should get the currentPaymentAmount from paymentIntentState?
      if (selectedFsOption === FundingOption.Card) {
        return {
          paymentAmount: currentPaymentAmount,
          ...selectedOptionKeyPayload,
          donations: getSelectedCharities(),
          fundingOption: selectedFsOption,
          card: {
            card: creditDebitCard.cardNo.replace(/\s/g, ""),
            cvc: creditDebitCard.securityCode,
            cardType: getCardNetworkByShortCode(creditDebitCard.cardType),
            expiry: creditDebitCard.cardExpiry,
            zip: creditDebitCard.zipCode,
            holderName:
              creditDebitCard.firstName + " " + creditDebitCard.lastName,
            saveToWallet: creditDebitCard.saveToWallet,
          },
          paymentDate: paymentIntentState.paymentDate,
        };
      } else if (selectedFsOption === FundingOption.BankAccount) {
        return {
          paymentAmount: currentPaymentAmount,
          ...selectedOptionKeyPayload,
          donations: getSelectedCharities(),
          fundingOption: selectedFsOption,
          bank: {
            bankNumber: bankAccount.bankAccountNumber,
            routingNumber: bankAccount.routingNumber,
            accountType: bankAccount.accountType,
            holderName: bankAccount.accountOwnerName,
            saveToWallet: bankAccount.saveToWallet,
          },
          paymentDate: paymentIntentState.paymentDate,
        };
      } else if (selectedFsOption === FundingOption.Wallet) {
        let fundingType = wallet.fundingOption;
        if (wallet.fundingOption === "Card") {
          if (wallet.presentableMOPCode === "AX") {
            fundingType = "CreditCardAmex";
          } else {
            fundingType = "CreditCard";
          }
        }
        return {
          paymentAmount: currentPaymentAmount,
          ...selectedOptionKeyPayload,
          donations: getSelectedCharities(),
          fundingOption: selectedFsOption,
          wallet: {
            fundingSourceId: wallet.fundingSourceId,
            type: fundingType,
          },
          paymentDate: paymentIntentState.paymentDate,
        };
      }

      return null;
    };

  const onSubmit = (data: any) => {
    // @ts-ignore
    //buttonRef.current.setLoadingToTrue();
    //setLoading(true);
    // - set up next step in ez-pay flow
  };

  const reviewDigitalPayment = (paymentData: DigitalPaymentData) => {
    setMethodModalIsOpenToFalse();

    // update payment intent
    const digitalWallet: IDigitalWallet = {
      cardType: paymentData.cardType,
      paymentToken: paymentData.paymentToken,
    };
    dispatch({
      type: ACTION.ADD_DIGITAL_WALLET,
      payload: digitalWallet,
    });
    const receiptInfo: IReceiptInfo = {
      mobileNumber: paymentData.mobileNumber ?? "",
      mobileAlert: false,
      email: paymentData.email,
      emailAlert: false,
      alertEnabled: false,
    };

    dispatch({ type: ACTION.ADD_RECEIPTINFO, payload: receiptInfo });
    dispatch({
      type: ACTION.ADD_PAYMENT_METHOD,
      payload: paymentData.paymentMethod,
    });
    dispatch({
      type: ACTION.ADD_SERVICE_FEE,
      payload: paymentData.serviceFee,
    });
  };

  const resetSelectedPaymentMethod = () => {
    // TODO: review this further. May be we need to create a state in Redux where we can store
    // the payment method information. May be this can be the new "payable payment intent state".
    // The new payable payment intent doesn't currently have accomodations for that and may be we
    // have to expand it???
    // There are new payment method modal components (bank, credit & debit cards) that
    // supposedly use/update their state to the new payment intent state because
    // there are design changes for those modals. But that doesn't necessarily mean
    // that we can't use the old payment intent state with those new components.
    // If there is no need for a new payable payment intent state then just update
    // those new components to use the old payment intent state.
    // For now we're resorting to using the old payment intent state so that we can run this page with no errors.
    paymentIntentState.creditDebitCard = null;
    paymentIntentState.bankAccount = null;
    paymentIntentState.wallet = undefined;
    paymentIntentState.digitalWallet = undefined;
  };

  const verifyPaymentAmountForDigitalWallet = () => {
    const isValidAmount = isAmountPayable();
    if (!isValidAmount.isValid) {
      setMethodModalIsOpenToFalse();
      return { isValid: false };
    }
    return { isValid: true };
  };

  const handlePrepareIntentErrorForDigitalWallet = (
    err: any,
    payablePrepareRequest: payablePaymentIntentPrepareRequest,
  ) => {
    resetSelectedPaymentMethod();
    handlePayablePrepareIntentErrors(err, payablePrepareRequest);
    setMethodModalIsOpenToFalse();
  };

  const isAmountPayable = () => {
    // TODO: review this payable null check as we shouldn't have to check this again here since we're
    // throwing an error in the render cycle if the payable is not available in the session.
    // So it should not even be possible to get here.
    if (!payableSession?.payable) {
      return { isValid: false };
    }

    // No payment option selected yet, so there's no amount due to compare against.
    // Report it as an invalid payment instead of an overpayment against a $0 amount due.
    if (amountPending) {
      setPaymentErrorMessage(t("payment-amount-invalid"));
      setErrorMessageInBanner(t("payment-amount-invalid"));
      return { isValid: false };
    }

    // TODO: shouldn't minAmount be fetched from the payable type configuration? It may not always be 1.
    const minAmount = 1;
    if (currentPaymentAmount === 0) {
      setPaymentErrorMessage(t("overpayment-enabled-no-amount-error"));
      setErrorMessageInBanner(t("payment-amount-invalid"));
      return { isValid: false };
    }

    // TODO: review if we can eliminate the paymentAllowedState and get the payment settings from the payable type definition instead?
    if (payableType.allowPartialPayment && currentPaymentAmount < minAmount) {
      setPaymentErrorMessage(t("invalid-min-payment-amount"));
      setErrorMessageInBanner(t("invalid-minimum-payment-banner"));
      return { isValid: false };
    }

    if (
      paymentAllowedState === PaymentAllowed.BilledAmountOrLess &&
      currentPaymentAmount > amountDue
    ) {
      setPaymentErrorMessage(
        t("overpayment-disabled-amount-error", {
          amount: amountDue,
        }),
      );
      setErrorMessageInBanner(t("overpayment-disabled-banner-error"));
      return { isValid: false };
    }

    if (
      paymentAllowedState === PaymentAllowed.BilledAmountOnly &&
      currentPaymentAmount !== amountDue
    ) {
      setPaymentErrorMessage(
        t("over-and-partial-payment-disabled-banner-error"),
      );
      setErrorMessageInBanner(
        t("over-and-partial-payment-disabled-banner-error"),
      );
      return { isValid: false };
    }

    // if no error, reset the error message
    setBannerState((prev) => ({
      ...prev,
      active: false,
      variant: "error",
      messageLabel: "",
    }));
    return { isValid: true };
  };

  const checkIsCashOnly = () => {
    if (
      !payablePaymentIntentState?.fundingSources ||
      payablePaymentIntentState?.fundingSources.length === 0
    )
      return false;

    if (
      payablePaymentIntentState?.fundingSources.length === 1 &&
      payablePaymentIntentState?.fundingSources.includes(FundingOption.Cash)
    )
      return true;

    return false;
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const payPalWrapper = useMemo(() => {
    if (payableSession.paypalConfiguration !== undefined) {
      return (
        <PayablePayPalButtonWrapper
          currency="USD"
          amount={currentPaymentAmount + charitiesTotal}
          paymentIntentId={payableSession.paymentIntentId}
          clientId={finalBiller.clientId}
          reviewDigitalPayment={reviewDigitalPayment}
          paymentMethod={paymentIntentState.paymentMethod}
          config={payableSession.paypalConfiguration}
          disabled={amountPending}
          verifyPaymentAmount={verifyPaymentAmountForDigitalWallet}
          onPrepareIntentError={handlePrepareIntentErrorForDigitalWallet}
        />
      );
    }
    return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paymentIntentState.paymentAmount,
    paymentIntentState.paymentMethod,
    finalBiller.clientId,
    paymentIntentState.intentId,
    // The button's amount (and the closures validating it) must follow the selected
    // payment option / donations, otherwise PayPal keeps showing the previous amount.
    currentPaymentAmount,
    charitiesTotal,
    selectedAmountOption?.optionKey,
    amountPending,
  ]);

  const getSelectedPaymentMethod = (
    payablePrepareRequest: payablePaymentIntentPrepareRequest,
  ) => {
    if (payablePrepareRequest.fundingOption === FundingOption.Card) {
      return "Card";
    } else if (
      payablePrepareRequest.fundingOption === FundingOption.BankAccount
    ) {
      return "BankAccount";
    } else if (payablePrepareRequest.fundingOption === FundingOption.Wallet) {
      if (payablePrepareRequest.wallet?.type === "Card") {
        return "Card";
      } else if (payablePrepareRequest.wallet?.type === "BankAccount") {
        return "BankAccount";
      }
    }
    // return selected digital wallet
    return payablePrepareRequest.fundingOption;
  };
  const setErrorMessageInBanner = (message: string, label?: string) => {
    setBannerState((prev) => ({
      ...prev,
      active: true,
      variant: "error",
      messageLabel: label ?? message,
      messageBody: label ? message : undefined,
      withClose: true,
      withHyperlink: false,
      withIcon: true,
    }));
  };

  const handlePayablePrepareIntentErrors = (
    err: any,
    payablePrepareRequest: payablePaymentIntentPrepareRequest,
  ) => {
    const selectedPaymentMethod = getSelectedPaymentMethod(
      payablePrepareRequest,
    );
    // A stored wallet card reports "Wallet" as the selected method, with the
    // card brand ("CreditCard"/"CreditCardAmex") on wallet.type.
    const walletType = payablePrepareRequest.wallet?.type;
    const isCardPaymentMethod =
      selectedPaymentMethod === "Card" ||
      walletType === "CreditCard" ||
      walletType === "CreditCardAmex";
    // New error codes (363/375/377) carry the min amount info in the code itself,
    // the legacy codes (30/31) use errorMessageType.
    const isMinAmountError =
      err.errorMessageType === "MinAmount" ||
      ["363", "375", "377"].includes(String(err.errorMessageCode));
    // New codes for partial/over payment not allowed.
    const isPartialPaymentNotAllowedError = ["368", "369", "370"].includes(
      String(err.errorMessageCode),
    );

    switch (err.errorMessageCode) {
      // if payment method is invalid, code will be 24/25/26/45 (legacy)
      // or 325/328/373/380/381/382/391 (new)
      case "24":
      case "25":
      case "26":
      case "45":
      case "325":
      case "328":
      case "373":
      case "380":
      case "381":
      case "382":
      case "391":
        setErrorMessageInBanner(t("payment-method-invalid"));
        return;

      // if payment velocity error, code will be 50/51 (legacy) or 389/390 (new)
      case "50":
      case "51":
      case "389":
      case "390":
        setErrorMessageInBanner(t("payment-velocity-exceeded-error"));
        return;

      // if payment amount is invalid, code will be 30/31 (legacy)
      // or 363/364/375/376/377 (min/max) and 368/369/370 (partial/over payment not allowed) (new)
      case "30":
      case "31":
      case "363":
      case "364":
      case "375":
      case "376":
      case "377":
      case "368":
      case "369":
      case "370":
        if (
          isPartialPaymentNotAllowedError ||
          (!payableType.allowPartialPayment &&
            currentPaymentAmount > err.errorMessageValue)
        ) {
          if (
            isCardPaymentMethod ||
            selectedPaymentMethod === "GooglePay" ||
            selectedPaymentMethod === "ApplePay" ||
            selectedPaymentMethod === "PayPal" ||
            selectedPaymentMethod === "Venmo"
          ) {
            setErrorMessageInBanner(
              t("invalid-amount-partial-payments-card-banner", {
                method: isCardPaymentMethod ? t("card") : selectedPaymentMethod,
                phone: phoneFormat(contactUs.properties.phone),
              }),
            );
            return;
          } else {
            setErrorMessageInBanner(
              t("invalid-amount-partial-payments-bank-banner", {
                phone: phoneFormat(contactUs.properties.phone),
              }),
            );
            return;
          }
        } else if (payableSession.charities != undefined) {
          // TODO: review this again, no idea how having charities relates to the error handling here?
          if (isMinAmountError) {
            if (
              isCardPaymentMethod ||
              selectedPaymentMethod === "GooglePay" ||
              selectedPaymentMethod === "ApplePay" ||
              selectedPaymentMethod === "PayPal" ||
              selectedPaymentMethod === "Venmo"
            ) {
              setErrorMessageInBanner(
                t("invalid-min-amount-card-banner", {
                  amount: err.errorMessageValue,
                  method: isCardPaymentMethod
                    ? t("card")
                    : selectedPaymentMethod,
                }),
              );
              return;
            } else {
              setErrorMessageInBanner(
                t("invalid-min-amount-bank-banner", {
                  amount: err.errorMessageValue,
                }),
              );
              return;
            }
          } else if (
            isCardPaymentMethod ||
            selectedPaymentMethod === "GooglePay" ||
            selectedPaymentMethod === "ApplePay" ||
            selectedPaymentMethod === "PayPal" ||
            selectedPaymentMethod === "Venmo"
          ) {
            setErrorMessageInBanner(
              t("invalid-max-amount-card-banner", {
                amount: err.errorMessageValue,
                method: isCardPaymentMethod ? t("card") : selectedPaymentMethod,
              }),
            );
            return;
          } else {
            setErrorMessageInBanner(
              t("invalid-max-amount-bank-banner", {
                amount: err.errorMessageValue,
              }),
            );
            return;
          }
        } else if (isMinAmountError) {
          if (
            isCardPaymentMethod ||
            selectedPaymentMethod === "GooglePay" ||
            selectedPaymentMethod === "ApplePay" ||
            selectedPaymentMethod === "PayPal" ||
            selectedPaymentMethod === "Venmo"
          ) {
            setErrorMessageInBanner(
              t("invalid-min-amount-card-banner", {
                amount: err.errorMessageValue,
                method: isCardPaymentMethod ? t("card") : selectedPaymentMethod,
              }),
            );
            setPaymentErrorMessage(
              t("payment-min-amount-error", {
                amount: err.errorMessageValue,
              }),
            );
            return;
          } else {
            setErrorMessageInBanner(
              t("invalid-min-amount-bank-banner", {
                amount: err.errorMessageValue,
              }),
            );
            setPaymentErrorMessage(
              t("payment-min-amount-error", {
                amount: err.errorMessageValue,
              }),
            );
            return;
          }
        } else if (
          isCardPaymentMethod ||
          selectedPaymentMethod === "GooglePay" ||
          selectedPaymentMethod === "ApplePay" ||
          selectedPaymentMethod === "PayPal" ||
          selectedPaymentMethod === "Venmo"
        ) {
          setErrorMessageInBanner(
            t("invalid-max-amount-card-banner", {
              amount: err.errorMessageValue,
              method: isCardPaymentMethod ? t("card") : selectedPaymentMethod,
            }),
          );
          setPaymentErrorMessage(
            t("payment-max-amount-error", {
              amount: err.errorMessageValue,
            }),
          );
          return;
        } else {
          setErrorMessageInBanner(
            t("invalid-max-amount-bank-banner", {
              amount: err.errorMessageValue,
            }),
          );
          setPaymentErrorMessage(
            t("payment-max-amount-error", {
              amount: err.errorMessageValue,
            }),
          );
          return;
        }
      default:
        setErrorMessageInBanner(
          t("process-payment-error"),
          t("payment-failed"),
        );
        return;
    }
  };

  const handleContinueClick = () => {
    // validate required fields
    const isAmountValid = isAmountPayable();
    if (!isAmountValid.isValid) {
      return;
    }

    if (bankAccount == null && creditDebitCard == null && wallet == null) {
      setPaymentMethodError(t("no-payment-method-selected-error"));
    }

    // Validate saved card type is still supported
    if (wallet?.fundingOption === "Card") {
      const presentableCode = wallet.presentableMOPCode;
      const supportedMopCodes = payableSession?.methodOfPayments ?? [];
      if (!supportedMopCodes.includes(presentableCode)) {
        setErrorMessageInBanner(t("payment-method-invalid"));
        return;
      }
    }

    if (
      paymentIntentState.charities != undefined &&
      paymentIntentState.charities?.filter((c) => c.selected && c.amount == 0)
        .length > 0
    ) {
      setErrorMessageInBanner(t("donation-amount-required"));
      return;
    }

    if (
      paymentIntentState.charities != undefined &&
      paymentIntentState.charities?.filter((c) => c.selected && c.amount > 1)
        .length !=
        paymentIntentState.charities?.filter((x) => x.selected).length
    ) {
      setErrorMessageInBanner(t("donation-amount-invalid"));
      return;
    }

    setBannerState((prev) => ({
      ...prev,
      active: false,
      variant: "error",
      messageLabel: "",
    }));

    const payablePrepareRequest = getPayablePrepareRequest();

    if (payablePrepareRequest === null) {
      console.error("Prepare request is null");
      return;
    }

    dispatch(setAppLoading(true));

    postPayablePrepareIntent(payablePrepareRequest)
      .catch((err) => {
        console.error("Error calling payable prepare endpoint", err);
        handlePayablePrepareIntentErrors(err, payablePrepareRequest);
        return Promise.reject(err);
      })
      .then(() => getPayableFromSession(getDomain()))
      .then((response: IPayableSessionData) => {
        dispatch(setPayableSession(response));
      })
      .catch((err) => {
        console.error("Error fetching payable session after prepare", err);
        return Promise.reject(err);
      })
      .then(() => navigate(routing.payableReview.index()))
      .catch((err) => {
        console.log("error = ", err);
      })
      .finally(() => dispatch(setAppLoading(false)));
  };

  let maxScheduledDays = -1;
  if (payableType.maxScheduledDays && payableType.allowScheduledPayment) {
    maxScheduledDays = payableType.maxScheduledDays;
  }

  let maxDueDate = moment().startOf("day").toDate();

  if (maxScheduledDays !== undefined && maxScheduledDays > 0) {
    const utcMaxDueDate = moment.utc(payableSession?.payable?.dueDate);

    if (utcMaxDueDate.isValid()) {
      maxDueDate = new Date(
        utcMaxDueDate.year(),
        utcMaxDueDate.month(),
        utcMaxDueDate.date(),
      );
    }

    maxDueDate.setDate(maxDueDate.getDate() + maxScheduledDays);
  }

  const futureDateDisabled =
    !_.isEmpty(upcomingPayment) ||
    moment(maxDueDate).isBefore(moment().startOf("day").toDate()) ||
    maxScheduledDays <= 0;

  let showDateDisabledMessage = false;

  if (
    maxScheduledDays > 0 &&
    moment(maxDueDate).isBefore(moment().startOf("day").toDate())
  ) {
    showDateDisabledMessage = true;
  }

  const setCashView = (method: string) => {
    if (method === "CashView") {
      setEmailBannerState(false);
      setSmsBannerState(false);
    } else if (method === "EmailModalSuccessClose") {
      setEmailBannerState(true);
      setSmsBannerState(false);
    } else if (method === "SmsModalSuccessClose") {
      setEmailBannerState(false);
      setSmsBannerState(true);
    } else {
      setEmailBannerState(false);
      setSmsBannerState(false);
    }
  };

  const handleEmailInputChange = (value: string) => {
    setEmailAddress(value);
  };

  const handleSmsInputChange = (value: string) => {
    setPhoneNumber(value);
  };

  // Account Messages

  const defaultBannerProps: Omit<BannerProps, "messageLabel"> = {
    active: true,
    variant: "info",
    withClose: true,
    withHyperlink: false,
    withIcon: true,
  };

  function prepareAccountMessages(messages: string[]): BannerProps[] {
    return messages.map((message) => ({
      ...defaultBannerProps,
      messageLabel: message,
    }));
  }

  // Method to get messages by keys
  const getMessagesByKeys = (keys: string[]): string[] => {
    const payableTypeKrn = payableSession?.payable?.typeKrn ?? null;
    return keys.reduce((messages: string[], key: string) => {
      const resolvedLabel = getLabelWithKrn(
        labelSourceWithKrn.labelsWithKrn,
        payableTypeKrn,
        key,
      );

      if (resolvedLabel !== key) {
        messages.push(resolvedLabel);
      }
      return messages;
    }, []);
  };

  function getAccountMessages(): string[] {
    if (
      payableSession?.payable &&
      payableSession?.payable.messageCodes.length > 0 &&
      labelSourceWithKrn.labelsWithKrn &&
      Object.keys(labelSourceWithKrn.labelsWithKrn).length > 0
    ) {
      const messages = getMessagesByKeys(
        payableSession?.payable?.messageCodes ?? [],
      );
      return messages;
    }
    return [];
  }

  const accountMessages = prepareAccountMessages(getAccountMessages());

  if (
    payableSession?.payable?.payableAmount === null &&
    payableSession?.payable?.category === "account" &&
    !hasMultiplePaymentOptions
  ) {
    return (
      <ErrorPayableDashboard
        visible={true}
        biller={finalBiller}
        accountNumber={payableSession?.payable?.accountNumber}
      />
    );
  }

  if (
    payableSession?.payable?.payableAmount === null &&
    payableSession?.payable?.category === "invoice" &&
    !hasMultiplePaymentOptions
  ) {
    return (
      <ErrorPayableDashboard
        visible={true}
        biller={finalBiller}
        invoiceNumber={payableSession?.payable?.invoiceNumber}
      />
    );
  }

  if (
    !payableType?.allowOverPayment &&
    payableSession?.payable?.payableAmount === 0 &&
    payableSession?.payable?.category === "account"
  ) {
    return (
      <ErrorPayableDashboard
        visible={true}
        biller={finalBiller}
        accountNumber={payableSession?.payable?.accountNumber}
      />
    );
  }

  if (
    !payableType?.allowOverPayment &&
    payableSession?.payable?.payableAmount === 0 &&
    payableSession?.payable?.category === "invoice" &&
    !hasMultiplePaymentOptions
  ) {
    return (
      <ErrorPayableDashboard
        visible={true}
        biller={finalBiller}
        invoiceNumber={payableSession?.payable?.invoiceNumber}
      />
    );
  }

  if (isDataLoading) {
    return <DashboardSkeleton visible={true} />;
  }

  // RCP ONLY WORKFLOW - Ensure there's no payment intent id and the only function option is cash
  if (
    !payableSession?.paymentIntentId &&
    payableSession?.fundingOptions?.length === 1 &&
    payableSession?.fundingOptions[0] === FundingOption.Cash
  ) {
    navigate(routing.payableCash.index());
  }

  const selectedAmountOptionDisplay = selectedAmountOption ? (
    currencyFormatter.format(selectedAmountOption.optionAmount)
  ) : (
    <span className="payable-amount-pending-dash">-</span>
  );

  const finalAmountDue = hasMultiplePaymentOptions
    ? selectedAmountOptionDisplay
    : currencyFormatter.format(payableSession.payable.payableAmount ?? 0);

  const hidePaymentAmountRow = 
    (!payableType.allowPartialPayment && !hasMultiplePaymentOptions) ||
    // Cash normally pays the full amount, but with multiple payment options the user
    // still has to pick which option the barcode is generated for.
    (isCashView && !hasMultiplePaymentOptions);

  const multiplePaymentOptionsButtonLabel = selectedAmountOption
    ? currencyFormatter.format(selectedAmountOption.optionAmount)
    : t("select-one");

  // Invoices with multiple payment options show the selected option's label under "Amount".
  const selectedAmountOptionLabel =
    payableSession?.payable?.category?.toLowerCase() === "invoice" &&
    hasMultiplePaymentOptions &&
    selectedAmountOption
      ? getLabelWithKrn(
          labelSourceWithKrn.labelsWithKrn,
          payableSession?.payable?.typeKrn ?? null,
          selectedAmountOption.labelKey,
        )
      : null;

  // ButtonRow types staticLabel as string but renders it as a React child.
  const amountStaticLabel = (selectedAmountOptionLabel ? (
    <span className="ez-pay-amount-static-label">
      <span>{t("amount")}</span>
      <span
        className="ez-pay-amount-static-label-option"
        data-cy="payment-amount-option-label"
      >
        {selectedAmountOptionLabel}
      </span>
    </span>
  ) : (
    `${t("amount")}`
  )) as unknown as string;

  const isCustomPaymentAmount =
    currentPaymentAmount !== payableSession?.payable?.payableAmount ||
    payableSession?.payable?.payableAmount === 0;

  const singlePaymentAmountButtonLabel = isCustomPaymentAmount
    ? currencyFormatter.format(currentPaymentAmount)
    : t("full-balance");

  const paymentAmountButtonLabel = hasMultiplePaymentOptions
    ? multiplePaymentOptionsButtonLabel
    : singlePaymentAmountButtonLabel;

  return (
    <div className="ez-pay-dashboard-page">
      {/* banner for generic info and error */}
      <div className="ez-pay-payable-banner">
        <Banner
          {...bannerState}
          data-cy={
            bannerState?.variant
              ? `banner-${bannerState.variant}-content`
              : "banner-info-content"
          }
          onClose={() => setBannerState((prev) => ({ ...prev, active: false }))}
        ></Banner>

        {/* banner for account/restriction messages */}
        {accountMessages.map(
          (banner) =>
            banner.active && (
              <Banner
                key={banner.messageLabel}
                variant={banner.variant}
                messageLabel={banner.messageLabel}
                withClose={banner.withClose}
                withIcon={banner.withIcon}
                isMobile={isMobile}
                data-cy={
                  banner?.variant
                    ? `banner-${banner.variant}-content`
                    : "banner-info-content"
                }
              />
            ),
        )}
        {emailBannerState && (
          <Banner
            autoCloseTimer={8000}
            messageLabel={t("cash-barcode-sent", {
              phoneOrEmail: emailAddress,
            })}
            variant="success"
            withIcon
            withClose
            data-cy="banner-success-content"
          />
        )}
        {smsBannerState && (
          <Banner
            autoCloseTimer={8000}
            messageLabel={t("cash-barcode-sent", {
              phoneOrEmail: phoneNumber,
            })}
            variant="success"
            withIcon
            withClose
            data-cy="banner-success-content"
          />
        )}
      </div>
      <div className="payable-ez-pay-balance-frame">
        <PageLoader />
        <div className="ez-pay-balance-top">
          <PayableCardHeader />

          <div
            className={
              isFreeform && !isMultiBusinessUnitSupported
                ? "ez-pay-balance-row single-business-unit"
                : "ez-pay-balance-row"
            }
          >
            {!isFreeform && (
              <div className="ez-pay-balance-content-center-amount-due-row">
                {t("amount-due")}
                <Tooltip
                  direction={
                    isMobile ? TooltipEnums.TopLeft : TooltipEnums.Right
                  }
                  content={t("amount-due-tooltip")}
                  classNames="amount-due-row-tooltip"
                  data-cy="amount-due-tooltip"
                  variant="rich"
                />
              </div>
            )}
            {isFreeform && isMultiBusinessUnitSupported && (
              <div className="ez-pay-balance-content-center-amount-due-row">
                {getLabelWithKrn(
                  labelSourceWithKrn.labelsWithKrn,
                  payableSession?.payable?.typeKrn,
                  payableSession?.payable?.businessUnitCode,
                )}
              </div>
            )}
            <div className="ez-pay-balance-content-center">
              <div
                className="ez-pay-balance-label-large"
                data-cy="final-amount-due"
              >
                {finalAmountDue}
              </div>
            </div>

            {!hasMultiplePaymentOptions &&
            (payableSession?.payable?.category === "account" ||
              payableSession?.payable?.category === "invoice") ? (
              <div className="ez-pay-balance-content-center-amount-due-row">
                {/* "{{payableName}} number" i.e. "Utility account number" or "Parking ticket number" */}
                {payableSession?.payable?.category === "account"
                  ? getLabelWithKrn(
                      labelSourceWithKrn.labelsWithKrn,
                      payableSession?.payable?.typeKrn,
                      "accountNumber",
                    )
                  : getLabelWithKrn(
                      labelSourceWithKrn.labelsWithKrn,
                      payableSession?.payable?.typeKrn,
                      "invoiceNumber",
                    )}
                :
                <span
                  className="ez-pay-balance-cell-value"
                  data-cy="balance-account-number"
                >
                  {/* TODO: review this, there must be a better way to show the invoice/account number instead of having to cast the payable to the correct type. */}
                  {payableSession?.payable?.category === "account"
                    ? (payableSession?.payable as AccountPayable).accountNumber
                    : (payableSession?.payable as InvoicePayable).invoiceNumber}
                </span>
              </div>
            ) : null}

            <div className="ez-pay-balance-content-center-small">
              {hasMultiplePaymentOptions && (
                <div className="ez-pay-balance-content-cell">
                  {t("contract-number")}:
                  <span
                    className="ez-pay-balance-cell-value"
                    data-cy="contract-number"
                  >
                    {(payableSession?.payable as InvoicePayable).invoiceNumber}
                  </span>
                </div>
              )}
              {!hasMultiplePaymentOptions &&
                payableSession?.payable?.category === "invoice" && (
                  <div className="ez-pay-balance-content-cell">
                    {t("invoiced-amount")}:
                    <span
                      className="ez-pay-balance-cell-value"
                      data-cy="account-number"
                    >
                      {currencyFormatter.format(
                        (payableSession?.payable as InvoicePayable)
                          .billedAmount ?? 0,
                      )}
                    </span>
                  </div>
                )}
              {!isFreeform && payableSession?.payable?.dueDate && (
                <div className="ez-pay-balance-content-cell">
                  {t("due-date")}:
                  <span
                    className="ez-pay-balance-cell-value"
                    data-cy="final-due-date"
                  >
                    {payableSession?.payable?.dueDate
                      ? new Intl.DateTimeFormat(i18n.language, {
                          timeZone: "UTC",
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        }).format(new Date(payableSession?.payable?.dueDate))
                      : ""}
                  </span>
                </div>
              )}
            </div>

            <div>
              {!_.isEmpty(upcomingPayment) && (
                <>
                  <div className="ez-pay-balance-content-center-small">
                    <div className="ez-pay-scheduled-payment-content-cell">
                      {t("scheduled-payment")}{" "}
                      {dateFormatter.shortFormat(
                        new Date(upcomingPayment.paymentDate),
                      )}{" "}
                      <span className="ez-pay-schedule-payment-amount-cell-value">
                        {currencyFormatter.format(upcomingPayment.totalAmount)}
                      </span>
                    </div>
                  </div>
                  <br />
                </>
              )}
              {loadingStates.loadRecentUpcomingPayments && (
                <div className="ez-pay-recent-payment-button">
                  <SkeletonItem width="171px" height="32px" />
                </div>
              )}
              {!loadingStates.loadRecentUpcomingPayments &&
                (!_.isEmpty(upcomingPayment) || recentPayments.length > 0) && (
                  <div className="ez-pay-balance-content-center-small">
                    <Link
                      to={routing.payableRecentPayments.index()}
                      className="ez-pay-button-link"
                      data-cy="recent-payments"
                    >
                      {t("view-recent-payment")}
                    </Link>
                  </div>
                )}
            </div>
          </div>
          <div className="ez-pay-balance-divider"></div>

          {checkIsCashOnly() && (
            <div>
              <CashView
                biller={finalBiller}
                // TODO: fix this, passing the KRN for now just so that there are no errors.
                // But we need to be passing the account number for account payables and may be
                // invoice number for the invoice payables OR may be it's okay to pass the payable KRN.
                // All that will be determined once we finalize how cash/RCP payments are supposed to work
                // with generic payables.
                accountNumber={payableSession?.payable?.krn}
                billerLogo={imageResources?.properties?.logoURL ?? ""}
                flowCallback={setCashView}
                realm={finalBiller.realm}
                emailInputChange={handleEmailInputChange}
                smsInputChange={handleSmsInputChange}
              ></CashView>
            </div>
          )}

          {(paymentAllowedState === PaymentAllowed.AnyAmount ||
            paymentAllowedState === PaymentAllowed.BilledAmountOnly ||
            paymentAllowedState === PaymentAllowed.BilledAmountOrLess) &&
            !checkIsCashOnly() && (
              <form onSubmit={handleSubmit(onSubmit)}>
                <div
                  role="heading"
                  aria-level={2}
                  className="ez-pay-balance-no-frame"
                >
                  {t("payment-details")}
                </div>
                {hidePaymentAmountRow ? null : (
                  <div
                    className={`ez-pay-balance-button-container${
                      selectedAmountOptionLabel
                        ? " ez-pay-balance-button-container-auto-height"
                        : ""
                    }`}
                  >
                    <ButtonRow
                      buttonLabel={paymentAmountButtonLabel}
                      onClick={setAmountModalIsOpenToTrue}
                      staticLabel={amountStaticLabel}
                      /* @ts-ignore */
                      dataCy="button-row-payment-amount"
                      data-testid="payment-amount-button-row"
                      errorText={
                        paymentErrorMessage && paymentErrorMessage.length > 0
                          ? paymentErrorMessage
                          : ""
                      }
                    />
                  </div>
                )}

                <div className="ez-pay-balance-button-container">
                  <ButtonRow
                    buttonLabel={paymentMethodLabel}
                    onClick={setMethodModalIsOpenToTrue}
                    staticLabel={`${t("method")}`}
                    {...(providerTypeByFundingOption[paymentMethod] && {
                      providerType: providerTypeByFundingOption[paymentMethod],
                    })}
                    {...(!isCashView && {
                      creditCardLogoVariant:
                        creditDebitCard !== null
                          ? castToCardType(
                              creditDebitCard?.cardType.toLowerCase(),
                            )
                          : wallet &&
                              wallet.fundingOption === FundingOption.Card
                            ? castToCardType(
                                getCardNetworkByShortCode(
                                  wallet.presentableMOPCode,
                                ).toLocaleLowerCase(),
                              )
                            : undefined,
                    })}
                    /* @ts-ignore */
                    dataCy="buttonw-row-payment-method"
                    data-testid="method-button-row"
                    errorText={
                      bankAccount == null &&
                      creditDebitCard == null &&
                      wallet == null
                        ? paymentMethodError
                        : ""
                    }
                  />
                </div>
                {maxScheduledDays > 0 && !isCashView ? (
                  <div className="ez-pay-balance-button-container">
                    <ButtonRow
                      // eslint-disable-next-line max-len
                      disabled={futureDateDisabled || isDigitalPaymentSelected}
                      buttonLabel={
                        dateFormatter.shortIsoFormat(
                          paymentIntentState.paymentDate,
                          i18n.language,
                        ) === todayDateString
                          ? `${t("today", { ns: COMMON_NAMESPACE })}`
                          : dateFormatter.shortIsoFormat(
                              paymentIntentState.paymentDate,
                              i18n.language,
                            )
                      }
                      onClick={paymentDateClicked}
                      staticLabel={`${t("date", { ns: COMMON_NAMESPACE })}`}
                      dataCy="button-row-payment-date"
                      data-testid="payment-date-button"
                    />
                  </div>
                ) : null}

                {showDateDisabledMessage && (
                  <div className="ez-pay-balance-container-scheduled">
                    <div>
                      <img
                        src={CircleInfo}
                        alt="info icon"
                        width={16}
                        height={16}
                      />
                    </div>
                    <div>{t("scheduled-payment-disabled")}</div>
                  </div>
                )}
                {!_.isEmpty(upcomingPayment) && (
                  <div className="ez-pay-balance-button-container">
                    <Tooltip
                      direction={TooltipEnums.TopRight}
                      content={`${t("only-one-scheduled")}`}
                      data-cy="schedule-payment-tooltip"
                      variant="rich"
                    />{" "}
                    {t("upcoming-payment-reminder", {
                      date: dateFormatter.shortFormat(
                        new Date(upcomingPayment.paymentDate),
                      ),
                    })}
                    <button
                      onClick={() =>
                        navigate(routing.payableRecentPayments.index())
                      }
                      className="view-details"
                      data-cy="view-details"
                    >
                      {t("view-details")}
                    </button>
                  </div>
                )}

                {charities &&
                  charities.length > 0 &&
                  !isFreeform &&
                  !isCashView && (
                    <div className="ez-pay-charity-container">
                      {charities.map((charity) => (
                        <Donation
                          key={charity.code}
                          charity={charity}
                          onCharityAmountSelected={(amount: number) =>
                            handleCharityAmountChange(charity.code, amount)
                          }
                          onCharitySelected={(isSelected: boolean) =>
                            handleCharitySelection(charity.code, isSelected)
                          }
                        />
                      ))}
                    </div>
                  )}

                {charities &&
                  charities.length > 0 &&
                  charitiesState.map(
                    (charityState) =>
                      charityState.selected &&
                      charityState.amount > 0 && (
                        <div
                          key={charityState.charity.code}
                          className="ez-pay-donation-item-parent-container"
                        >
                          <div
                            className="ez-pay-donation-item-container"
                            data-testid="charities-container"
                          >
                            <div className="ez-pay-donation-item-row">
                              <div className="ez-pay-donation-item-label">
                                {t("donation-to", {
                                  name: charityState.charity.name,
                                })}
                              </div>
                              <div className="ez-pay-donation-item-amount">
                                {currencyFormatter.format(charityState.amount)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ),
                  )}

                <div className="ez-pay-payment-total-parent-container">
                  <div className="ez-pay-payment-total-container">
                    {/* TODO: review with design team if we need to show the amount due here again.
                    There's already a question asked about this in Figma but no decision on it yet. */}
                    <div className="ez-pay-payment-total-row">
                      <div className="ez-pay-payment-small-label">
                        {isFreeform
                          ? t("payment-amount")
                          : t("invoice-amount-due")}
                      </div>
                      <div
                        className={
                          paymentErrorMessage
                            ? "ez-pay-payment-total-amount-error"
                            : "ez-pay-payment-small-label"
                        }
                        data-cy="payment-due"
                      >
                        {amountPending ? (
                          <span className="payable-amount-pending-dash">-</span>
                        ) : (
                          currencyFormatter.format(currentPaymentAmount)
                        )}
                      </div>
                    </div>

                    <div className="ez-pay-payment-total-row">
                      <div className="ez-pay-payment-total-label">
                        {t("payment-total")}
                      </div>
                      <div
                        className={
                          paymentErrorMessage
                            ? "ez-pay-payment-total-amount-error"
                            : "ez-pay-payment-total-amount"
                        }
                        data-cy="payment-total"
                      >
                        {amountPending ? (
                          <span className="payable-amount-pending-dash">-</span>
                        ) : (
                          currencyFormatter.format(
                            currentPaymentAmount + charitiesTotal,
                          )
                        )}
                      </div>
                    </div>

                    <div
                      className="ez-pay-payment-total-label-info"
                      data-testid="process-payment-info"
                    >
                      {t("process-payment-info")}
                    </div>
                  </div>
                  {isCashView ? (
                    <div className="ez-pay-balance-row-noframe">
                      {/* @ts-ignore */}
                      <Button
                        className="validate-button"
                        // With multiple payment options a barcode needs a selected option.
                        // Without them (e.g. partial payment) a zero amount is still allowed.
                        disabled={amountPending}
                        onClick={() => {
                          if (amountPending) return;
                          navigate(routing.payableCash.index());
                        }}
                        data-cy="button-generate-barcode"
                        data-testid="generate-barcode-button"
                      >
                        {t("generate-barcode")}
                      </Button>
                    </div>
                  ) : (
                    <div
                      className="ez-pay-balance-row-noframe"
                      data-cy={
                        PAYMENT_METHOD_DATA_CY[paymentIntentState.paymentMethod]
                      }
                    >
                      {paymentIntentState.paymentMethod == GooglePay &&
                        payableSession.googlePayConfiguration && (
                          <PayableGooglePayButtonWrapper
                            country="US"
                            currencyCode="USD"
                            clientId={finalBiller.clientId}
                            paymentIntentId={paymentIntentState.intentId}
                            amount={currentPaymentAmount + charitiesTotal}
                            reviewDigitalPayment={reviewDigitalPayment}
                            verifyPaymentAmount={
                              verifyPaymentAmountForDigitalWallet
                            }
                            onPrepareIntentError={
                              handlePrepareIntentErrorForDigitalWallet
                            }
                            config={payableSession.googlePayConfiguration}
                            realm={payableSession.instanceInfo.realm}
                            methodOfPayments={payableSession.methodOfPayments}
                          ></PayableGooglePayButtonWrapper>
                        )}
                      {paymentIntentState.paymentMethod == ApplePay && (
                        <PayableApplePayButtonWrapper
                          country="US"
                          currencyCode="USD"
                          clientId={finalBiller.clientId}
                          paymentIntentId={paymentIntentState.intentId}
                          amount={currentPaymentAmount + charitiesTotal}
                          reviewDigitalPayment={reviewDigitalPayment}
                          verifyPaymentAmount={
                            verifyPaymentAmountForDigitalWallet
                          }
                          onPrepareIntentError={
                            handlePrepareIntentErrorForDigitalWallet
                          }
                          methodOfPayments={payableSession.methodOfPayments}
                        ></PayableApplePayButtonWrapper>
                      )}
                      {(paymentIntentState.paymentMethod == PayPal ||
                        paymentIntentState.paymentMethod == Venmo) &&
                        payableSession.paypalConfiguration &&
                        !methodModalIsOpen &&
                        payPalWrapper}
                      {paymentIntentState.paymentMethod != GooglePay &&
                        paymentIntentState.paymentMethod != PayPal &&
                        paymentIntentState.paymentMethod != ApplePay &&
                        paymentIntentState.paymentMethod != Venmo && (
                          <Button
                            className="validate-button"
                            onClick={handleContinueClick}
                            data-cy="button-continue"
                            data-testid="continue-button"
                          >
                            {t("continue", { ns: COMMON_NAMESPACE })}
                          </Button>
                        )}
                    </div>
                  )}
                </div>
                {amountSelection ? (
                  <PayableAmountOptionsModal
                    isOpen={amountModalIsOpen}
                    onClose={setAmountModalIsOpenToFalse}
                    typeKrn={payableSession?.payable?.typeKrn ?? null}
                    messageKey={amountSelection.messageKey}
                    options={amountSelection.options}
                    selectedOptionKey={selectedAmountOption?.optionKey ?? null}
                    updateCallBack={(option) => {
                      setSelectedAmountOption(option);
                      dispatch({
                        type: ACTION.ADD_SELECTED_AMOUNT_OPTION_KEY,
                        payload: option.optionKey,
                      });
                      updateCurrentPaymentAmount(option.optionAmount);
                      setAmountModalIsOpenToFalse();
                    }}
                  />
                ) : (
                  <>
                    {isMobile && (
                      <Sheet
                        isOpen={amountModalIsOpen}
                        onClose={() => setAmountModalIsOpenToFalse}
                        detent="content-height"
                        disableDrag={true}
                      >
                        <Sheet.Container>
                          <Sheet.Header>
                            <div className="react-modal-sheet-header">
                              <div
                                className="help-modal-header-text"
                                data-testid="select-amount-modal-header"
                              >
                                {t("select-amount")}
                              </div>
                              <button
                                className="modal-close"
                                onClick={setAmountModalIsOpenToFalse}
                                aria-label={`${t("close-modal", {
                                  ns: COMMON_NAMESPACE,
                                })}`}
                                data-cy="modal-close-x-payment-amount"
                              >
                                <img
                                  src={XButton}
                                  alt={`${t("close-modal", {
                                    ns: COMMON_NAMESPACE,
                                  })}`}
                                  className="modal-close-x"
                                />
                              </button>
                            </div>
                          </Sheet.Header>
                          <Sheet.Content>
                            <PaymentAmountModal
                              allowPartialPayment={
                                payableType.allowPartialPayment
                              }
                              allowOverPayment={payableType.allowOverPayment}
                              fullBalanceAmount={
                                payableSession?.payable?.payableAmount ?? 0
                              }
                              currentPaymentAmount={currentPaymentAmount}
                              updateCallBack={updateCurrentPaymentAmount}
                            />
                          </Sheet.Content>
                        </Sheet.Container>
                        <Sheet.Backdrop />
                      </Sheet>
                    )}
                    {!isMobile && (
                      // @ts-ignore
                      <Modal
                        isOpen={amountModalIsOpen}
                        ariaHideApp={false}
                        className="modal-frame-help"
                        shouldCloseOnOverlayClick={false}
                        onRequestClose={setAmountModalIsOpenToFalse}
                        style={customStyles}
                      >
                        <div className="modal-header">
                          <div
                            className="help-modal-header-text"
                            data-testid="select-amount-modal-header"
                          >
                            {t("select-amount")}
                          </div>
                          <button
                            className="modal-close"
                            onClick={setAmountModalIsOpenToFalse}
                            aria-label={`${t("close-modal", {
                              ns: COMMON_NAMESPACE,
                            })}`}
                            data-cy="modal-close-x-payment-amount"
                          >
                            <img
                              src={XButton}
                              alt={`${t("close-modal", { ns: COMMON_NAMESPACE })}`}
                              className="modal-close-x"
                            />
                          </button>
                        </div>
                        <PaymentAmountModal
                          allowPartialPayment={payableType?.allowPartialPayment}
                          allowOverPayment={payableType?.allowOverPayment}
                          fullBalanceAmount={
                            payableSession?.payable?.payableAmount ?? 0
                          }
                          currentPaymentAmount={currentPaymentAmount}
                          updateCallBack={updateCurrentPaymentAmount}
                        />
                      </Modal>
                    )}
                  </>
                )}
                <UnableToLoadAmountsModal
                  isOpen={unableToLoadAmountsModalIsOpen}
                  onClose={() => setUnableToLoadAmountsModalIsOpen(false)}
                  onRetry={handleRetryAmountOptions}
                />
                {isMobile && (
                  <Sheet
                    isOpen={methodModalIsOpen}
                    onClose={() => setMethodModalIsOpenToFalse}
                    detent="content-height"
                    disableDrag={true}
                  >
                    <Sheet.Container>
                      <Sheet.Header>
                        <div
                          className="react-modal-sheet-header"
                          data-testid="select-method-modal"
                        >
                          {displayMethodBack && (
                            <button
                              onClick={() => {
                                handleMethodChange("SelectPaymentMethodModal");
                              }}
                              className="modal-close-back"
                              aria-label={t("back", { ns: COMMON_NAMESPACE })}
                            >
                              <FontAwesomeIcon
                                icon={faArrowLeft}
                                size="xl"
                                aria-hidden="true"
                              />
                            </button>
                          )}
                          <div className="help-modal-header-text">
                            {t("select-method")}
                          </div>
                          <button
                            className="modal-close"
                            onClick={setMethodModalIsOpenToFalse}
                            aria-label={`${t("close-modal", {
                              ns: COMMON_NAMESPACE,
                            })}`}
                            data-cy="modal-close-x-payment-method"
                          >
                            <img
                              src={XButton}
                              alt={`${t("close-modal", {
                                ns: COMMON_NAMESPACE,
                              })}`}
                              className="modal-close-x"
                            />
                          </button>
                        </div>
                      </Sheet.Header>
                      <Sheet.Content style={{ overflow: "auto" }}>
                        <CurrentMethodComponent
                          billerLogo={imageResources?.properties?.logoURL}
                          flowCallback={handleMethodChange}
                          // TODO: there doesn't seem to be any component in "methodComponents"
                          // that accepts a payable KRN, so safe to remove it from here.
                          // payableKrn={payableKrn}
                          realm={finalBiller.realm}
                          biller={finalBiller}
                          amount={currentPaymentAmount}
                          billerConfiguration={billerConfiguration}
                          termsUrl={
                            externalLinks?.properties?.termsAndConditions
                          }
                          // TODO: probably safe to remove this as none of the components
                          // in "methodComponents" have a userAccount prop.
                          // userAccount={mockAccount}
                          closeModalCallBack={setMethodModalIsOpenToFalse}
                          reviewDigitalPayment={reviewDigitalPayment}
                          verifyPaymentAmount={
                            verifyPaymentAmountForDigitalWallet
                          }
                          onPrepareIntentError={
                            handlePrepareIntentErrorForDigitalWallet
                          }
                          showManagePaymentMethodsModal={() =>
                            setManagePaymentMethodModalIsOpen(true)
                          }
                          isOpen={setRetailerModalStatus}
                          setIsCashView={setIsCashView}
                        />
                      </Sheet.Content>
                    </Sheet.Container>
                    <Sheet.Backdrop />
                  </Sheet>
                )}
                {!isMobile && (
                  // @ts-ignore
                  <Modal
                    isOpen={methodModalIsOpen}
                    ariaHideApp={false}
                    className="modal-frame-help payable-dashboard-payment-modal"
                    shouldCloseOnOverlayClick={false}
                    onRequestClose={setMethodModalIsOpenToFalse}
                    style={customStyles}
                  >
                    <div
                      className="modal-header-with-back-button"
                      data-testid="select-method-modal"
                    >
                      {displayMethodBack && (
                        <button
                          onClick={() => {
                            handleMethodChange("SelectPaymentMethodModal");
                          }}
                          className="modal-close-back"
                          aria-label={t("back", { ns: COMMON_NAMESPACE })}
                        >
                          <FontAwesomeIcon
                            icon={faArrowLeft}
                            size="xl"
                            aria-hidden="true"
                          />
                        </button>
                      )}
                      <div className="help-modal-header-text">
                        {isCashView ? t("cash-method") : t("select-method")}
                      </div>
                      <button
                        className="modal-close"
                        onClick={setMethodModalIsOpenToFalse}
                        aria-label={`${t("close-modal", {
                          ns: COMMON_NAMESPACE,
                        })}`}
                        data-cy="modal-close-x--payment-method"
                      >
                        <img
                          src={XButton}
                          alt={`${t("close-modal", { ns: COMMON_NAMESPACE })}`}
                          className="modal-close-x"
                        />
                      </button>
                    </div>
                    <CurrentMethodComponent
                      billerLogo={imageResources?.properties?.logoURL}
                      flowCallback={handleMethodChange}
                      // TODO: there doesn't seem to be any component in "methodComponents"
                      // that accepts a payable KRN, so safe to remove it from here.
                      // payableKrn={payableKrn}
                      biller={finalBiller}
                      amount={currentPaymentAmount}
                      billerConfiguration={billerConfiguration}
                      termsUrl={externalLinks?.properties?.termsAndConditions}
                      // TODO: probably safe to remove this as none of the components
                      // in "methodComponents" have a userAccount prop.
                      // userAccount={mockAccount}
                      closeModalCallBack={setMethodModalIsOpenToFalse}
                      reviewDigitalPayment={reviewDigitalPayment}
                      verifyPaymentAmount={verifyPaymentAmountForDigitalWallet}
                      onPrepareIntentError={
                        handlePrepareIntentErrorForDigitalWallet
                      }
                      showManagePaymentMethodsModal={() =>
                        setManagePaymentMethodModalIsOpen(true)
                      }
                      isOpen={setRetailerModalStatus}
                      setIsCashView={setIsCashView}
                    />
                  </Modal>
                )}

                <ManagePaymentMethodsModal
                  isOpen={managePaymentMethodModalIsOpen}
                  onClose={() => setManagePaymentMethodModalIsOpen(false)}
                  isPayable
                />
                {/* TODO: review if the payableSession.payable.dueDate will have timezone information and how it will be displayed to the user. */}
                <PaymentDateModal
                  dueDate={
                    payableSession?.payable?.dueDate
                      ? (() => {
                          const utcDueDate = moment.utc(
                            payableSession?.payable?.dueDate,
                          );

                          if (!utcDueDate.isValid()) {
                            return new Date();
                          }

                          return new Date(
                            utcDueDate.year(),
                            utcDueDate.month(),
                            utcDueDate.date(),
                          );
                        })()
                      : new Date()
                  }
                  daysAfter={maxScheduledDays < 0 ? 0 : maxScheduledDays}
                  isOpen={dateModalIsOpen}
                  onClose={() => setDateModalIsOpen(false)}
                  biller={finalBiller}
                ></PaymentDateModal>
              </form>
            )}
          {paymentAllowedState === PaymentAllowed.NoPaymentAllowed &&
            !isDataLoading && (
              <div className="ez-pay-balance-row-noframe">
                <Button
                  type="button"
                  className="validate-button"
                  onClick={() => {
                    IamApi.ezpay.logout();
                    dispatch({ type: USER_LOGOUT });
                    dispatch(setPayableSession(undefined));
                  }}
                  dataCy="button-validate"
                >
                  {t("change-payable", {
                    ns: PAYMENT_RECEIPT_NAMESPACE,
                    name: finalBiller?.billerName ?? "",
                  })}
                </Button>
              </div>
            )}
          <ContactAndTocFooter biller={finalBiller} />
        </div>
      </div>
    </div>
  );
};
export default PayableDashboard;
